// The client event bus: one typed stream of gameplay, money and UI events for the audio engine,
// the HUD/onboarding UI and the renderer, so those lanes never reach into each other's modules.
//
// Who emits (each event has exactly one producer):
// - net/handlers.ts (`attachHandlers(…, bus)`) — server messages, at arrival: welcome, spawned,
//   playing, ownCellsChanged, ownDeath, leaderboard, moneyUpdate, ownValueChanged, cashOutState,
//   ownCashOutStart/Progress/Stop, cashOutDone, notice, cashOutTooEarly, payoutDelayed,
//   serverError, moneyError; and every intent written to the socket (GameSocket `sent`):
//   splitSent, ejectSent (hold-W repeats included), playSent, spectateSent, specialSent,
//   cashOutStartSent, cashOutCancelSent.
// - events/render-tap.ts (`RenderEventTap.afterSample`, called by app.ts right after
//   `store.sample(rt)`) — render time, in sync with what is drawn (audio §3): eat, ownMerge,
//   virusPopped, ownCellEaten, ownLastCellEaten, firstOwnCellDrawn.
// - app.ts — appState, uiClick (every menu button's click hook), moneyFlow.
//
// Who consumes (planned): audio (A lane: eat/ownMerge/virusPopped/ownLastCellEaten/ownDeath,
// splitSent/ejectSent, uiClick), UI (U1: HUD, cash-out ring/progress, results and cash-out
// screens; U2: onboarding hint — firstOwnCellDrawn, splitSent, ejectSent, cashOutStartSent,
// ownDeath, appState), render (R lanes: firstOwnCellDrawn for the undim/boot scene, cashOutState
// for the ring if they prefer events over polling `store.cashOutProgress`).
//
// Rules:
// - Listeners run synchronously inside the producer (a net handler, the frame loop). Keep them
//   cheap; never throw on purpose — a throwing listener is logged and the others still run.
// - Payloads may be pooled and reused (every render-time event, `eat` in particular): read them
//   during the call, copy what you keep. Never mutate a payload.
// - Events that carry no data use the frozen `NO_PAYLOAD` object.
//
// Implemented fully (contracts step). DOM-free.

import {
  type AmountKind,
  type CashOutDoneMessage,
  type DeathMessage,
  ERROR,
  type ErrorMessage,
  type LeaderboardSection,
  type NoticeMessage,
  type WelcomeMessage,
  type WelcomeMoneyInfo,
} from '@ballio/shared';
import type { AppState, MoneyFlowStep } from '../app.js';
import type { EatEvent } from '../game/world-store.js';
import { Emitter } from './emitter.js';

/** The payload of events that carry no data. */
export type NoPayload = Readonly<Record<never, never>>;
export const NO_PAYLOAD: NoPayload = Object.freeze({});

/** What own cells looked like when an intent was sent (newest FRAME; audio §3 rows 1–2 gating). */
export interface OwnSnapshot {
  /** Live own cells (0 while dead, spectating or on the menu). */
  readonly ownCells: number;
  /** Mass of the largest own cell (0 without cells). */
  readonly maxOwnMass: number;
}

/** The money-room ERROR codes 11–16 (protocol §5.6, money-mode §2.1), by name. */
export type MoneyErrorKind =
  | 'ticketRequired'
  | 'ticketInvalid'
  | 'ticketUsed'
  | 'moneyLifeActive'
  | 'stakeNotOffered'
  /** ERROR 16: the ticket was bought by another account than the socket's. */
  | 'ticketNotYours';

/**
 * Where a menu click came from (audio §3 row 7: every one of these plays `click`). UI components
 * call their injected `playClick(source)` dep; app.ts emits `uiClick` and the audio engine plays.
 */
export type UiClickSource =
  | 'play'
  | 'spectate'
  | 'settings'
  | 'close'
  | 'skin'
  | 'help'
  | 'mode'
  | 'party'
  | 'continue'
  | 'dialog'
  | 'stake'
  | 'buyIn'
  | 'cashOutScreen'
  | 'onboarding'
  /** The account row and the username dialog (sign in / out, copy wallet). */
  | 'account'
  | 'other';

export interface ClientEvents {
  // --- server messages (net/handlers.ts) ------------------------------------------------------

  /** WELCOME of a new socket (the store was reset first). `money` = the money block or null. */
  welcome: { readonly msg: WelcomeMessage; readonly money: WelcomeMoneyInfo | null };
  /** SPAWNED: a new own life id (cells not on screen yet). */
  spawned: { readonly playerId: number };
  /** The first FRAME with OWN after SPAWNED was applied (same moment as GameSink.playing). */
  playing: { readonly playerId: number };
  /** OWN changed the own cell count (split, merge, cells eaten), while alive. */
  ownCellsChanged: { readonly count: number; readonly prev: number };
  /** DEATH. `valueLost` = lamports lost in a money room, else null. */
  ownDeath: { readonly msg: DeathMessage; readonly valueLost: number | null };
  /**
   * A LEADERBOARD section was applied. `yourAmount` / `amountKind` come from the amounts trailer
   * (null without it): floor(score) in free rooms, lamports in money rooms (owner exception (a)).
   */
  leaderboard: {
    readonly section: LeaderboardSection;
    readonly yourRank: number;
    readonly yourAmount: number | null;
    readonly amountKind: AmountKind | null;
    readonly tick: number;
  };
  /** A FRAME MONEY section was applied (price and/or values). Read details from `store.money`. */
  moneyUpdate: { readonly solUsd: number; readonly ownValue: number | null };
  /** The own life's value changed (lamports). `prev` null = first value of this life. */
  ownValueChanged: { readonly value: number; readonly prev: number | null };
  /** CASH_OUT_STATE for any player (`own` = the current life). Progress is the server's 0–1. */
  cashOutState: {
    readonly playerId: number;
    readonly own: boolean;
    readonly active: boolean;
    readonly progress: number;
    readonly atMs: number;
  };
  /** Own hold started (the server accepted CASH_OUT_START). */
  ownCashOutStart: { readonly progress: number };
  /** Own hold refreshed (about once a second while holding). */
  ownCashOutProgress: { readonly progress: number };
  /**
   * Own hold stopped. `completed` = it reached the end (progress 1; CASH_OUT_DONE follows);
   * otherwise it was cancelled (key released, split/eject, death).
   */
  ownCashOutStop: { readonly progress: number; readonly completed: boolean };
  /** CASH_OUT_DONE: the life was cashed out (hold, idle, disconnect or shutdown). */
  cashOutDone: {
    readonly msg: CashOutDoneMessage;
    /** Lamports paid. */
    readonly value: number;
    /** Label price at payout (0 = unknown; fall back to `store.money.solUsd`). */
    readonly solUsd: number;
  };
  /** Every NOTICE (the specific ones below are emitted in addition). */
  notice: NoticeMessage;
  /** NOTICE cashOutTooEarly: whole seconds until cash-out is allowed. */
  cashOutTooEarly: { readonly secondsLeft: number };
  /** NOTICE payoutDelayed: the cash-out finished but the payout is held for review. */
  payoutDelayed: NoPayload;
  /** Every ERROR (the server closes right after). `money` = the money kind for codes 11–16. */
  serverError: { readonly msg: ErrorMessage; readonly money: MoneyErrorKind | null };
  /** ERROR 11–16 (ticket / money-life problems): show the buy-in error, not a connection one. */
  moneyError: { readonly code: number; readonly kind: MoneyErrorKind; readonly message: string };

  // --- intents written to the socket (net/handlers.ts, from GameSocket `sent`) ---------------

  /** A SPLIT was sent (key press, touch button). Audio gates the sound on the snapshot. */
  splitSent: OwnSnapshot;
  /** An EJECT was sent (key press, touch button, every hold-W repeat). */
  ejectSent: OwnSnapshot;
  /** A PLAY was sent; `ticket` = it carried a money-room entry ticket. */
  playSent: { readonly ticket: boolean };
  spectateSent: NoPayload;
  /** SPECIAL (Q while spectating: switch camera). */
  specialSent: NoPayload;
  cashOutStartSent: NoPayload;
  cashOutCancelSent: NoPayload;

  // --- render time (events/render-tap.ts) ------------------------------------------------------

  /** An eat record took effect at the render tick (pooled; see world-store EatEvent). */
  eat: EatEvent;
  /** Both cells own: a merge (audio `merge`, in addition to `eat`). */
  ownMerge: { readonly survivorId: number; readonly mergedId: number };
  /** A virus was eaten (popped) on screen, by anyone (audio `pop`, no ownership check). */
  virusPopped: {
    readonly virusId: number;
    readonly eaterId: number;
    readonly eaterPlayerId: number;
    /** The eater is an own cell. */
    readonly own: boolean;
  };
  /** An own cell was eaten by another player's cell (not a merge) and others remain. */
  ownCellEaten: {
    readonly cellId: number;
    readonly eaterPlayerId: number;
    readonly ownLeft: number;
  };
  /** The eat record that removed the last own cell (audio `death`, instead of `eat`). */
  ownLastCellEaten: { readonly cellId: number; readonly eaterPlayerId: number };
  /**
   * The first own cell of a life was drawn (ui-qol §3: undim starts; §9.1: onboarding hint
   * timer; visuals §11.1: boot scene off).
   */
  firstOwnCellDrawn: { readonly playerId: number };

  // --- app (app.ts) ----------------------------------------------------------------------------

  /** The ui-flow §2 state changed. */
  appState: { readonly state: AppState; readonly prev: AppState };
  /** A menu button was activated (audio `click`). */
  uiClick: { readonly source: UiClickSource };
  /** The money flow (stake select → buy-in → play) changed step. */
  moneyFlow: { readonly step: MoneyFlowStep; readonly prev: MoneyFlowStep };
}

export type ClientEventName = keyof ClientEvents;

export type ClientBus = Emitter<ClientEvents>;

function defaultOnError(error: unknown, type: PropertyKey): void {
  console.error(`[ball.io] "${String(type)}" listener failed`, error);
}

/**
 * A new bus. Listener errors go to `onError` (default `console.error`) and never stop the
 * producer or the other listeners.
 */
export function createClientBus(
  onError: (error: unknown, type: PropertyKey) => void = defaultOnError,
): ClientBus {
  return new Emitter<ClientEvents>({ onError });
}

/** ERROR code → money kind (codes 11–16), or null. */
export function moneyErrorKind(code: number): MoneyErrorKind | null {
  switch (code) {
    case ERROR.TICKET_REQUIRED:
      return 'ticketRequired';
    case ERROR.TICKET_INVALID:
      return 'ticketInvalid';
    case ERROR.TICKET_USED:
      return 'ticketUsed';
    case ERROR.MONEY_LIFE_ACTIVE:
      return 'moneyLifeActive';
    case ERROR.STAKE_NOT_OFFERED:
      return 'stakeNotOffered';
    case ERROR.TICKET_NOT_YOURS:
      return 'ticketNotYours';
    default:
      return null;
  }
}
